import { Component, Inject, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import { NgxRolesService } from 'ngx-permissions'
import { map, startWith } from 'rxjs/operators'
import * as R from 'ramda'
import * as fromRoot from '@tv3/store/state'
import { asLocalDate, SaveForm, sortAscend } from '@tokeet/tv3-platform'
import { selectAllRentals } from '@tokeet/tv3-platform'
import { holdEventTypes } from '@tv3/constants/hold-event-types'
import { HoldForm } from '@tv3/interfaces/forms/hold.form'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { notAllSpacesValidator } from '@tokeet/tv3-platform'
import { AddHoldEvent } from '@tv3/store/calendar/calendar.actions'
import { AuthService } from '@tv3/services/auth.service'
import { Rental } from '@tokeet/tv3-platform'
import * as moment from 'moment'
import { BehaviorSubject, merge } from 'rxjs'
import { Destroyable, untilDestroy } from '@tokeet/tv3-platform'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatCheckboxChange } from '@angular/material/checkbox'
import { AmplitudeService } from '@tv3/services/amplitude.service'

export interface AddHoldDialogInput {
  inquiry?: Inquiry
}

@Component({
  selector: 'app-add-hold-dialog',
  templateUrl: './add-hold-dialog.component.html',
  styleUrls: ['./add-hold-dialog.component.scss'],
  host: { class: 'modal-content' },
})
export class AddHoldDialogComponent extends Destroyable implements OnInit {
  form = this.fb.group({
    title: ['', [Validators.required, notAllSpacesValidator]],
    type: ['', [Validators.required]],
    start: ['', [Validators.required]],
    end: ['', [Validators.required]],
    expires: [''],
    rentals: [[], [Validators.required]],
    rolling: [0],
    color: [],
  })

  minDateDefault = moment().subtract(10, 'years').toDate()
  maxDateDefault = moment().add(10, 'years').toDate()

  types = sortAscend('name')(holdEventTypes)
  rentals$ = this.store.pipe(select(selectAllRentals)).pipe(map((rentals) => this.filterRentals(rentals)))

  minExpirationDate = moment().startOf('day').toDate()

  minEndDate = new BehaviorSubject<Date>(null)
  maxStartDate = new BehaviorSubject<Date>(null)

  constructor(
    public dialogRef: MatDialogRef<AddHoldDialogComponent>,
    private store: Store<fromRoot.State>,
    private fb: FormBuilder,
    private roleService: NgxRolesService,
    private auth: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: AddHoldDialogInput,
    private amplitudeService: AmplitudeService
  ) {
    super()
  }

  ngOnInit() {
    if (this.data && this.data.inquiry) {
      const rentalId = R.path(['rentalId'], this.data.inquiry)
      const arrive = asLocalDate(this.data.inquiry.guestArrive)
      let depart = asLocalDate(this.data.inquiry.guestDepart)
      const isSameDay = moment(moment(arrive).format('YYYY-MM-DD')).isSame(moment(moment(depart).format('YYYY-MM-DD')))

      if (isSameDay && arrive) {
        depart = moment(depart).clone().add(1, 'days').toDate()
      }

      this.form.patchValue({
        title: R.pathOr('', ['guestDetails', 'name'], this.data.inquiry),
        type: 3,
        start: arrive,
        end: depart,
        rentals: rentalId ? [rentalId] : [],
      })
    }

    this.form.patchValue({ type: 4 })

    merge(this.form.get('start').valueChanges, this.form.get('end').valueChanges)
      .pipe(startWith(null), untilDestroy(this))
      .subscribe(() => {
        this.setStartEndMinMax()
      })
  }

  setStartEndMinMax() {
    if (this.form.get('start').value) {
      const start = moment(this.form.get('start').value).add(1, 'days').startOf('day')
      this.minEndDate.next(start.toDate())
    }

    if (this.form.get('end').value) {
      const end = moment(this.form.get('end').value).subtract(1, 'days').endOf('day')
      this.maxStartDate.next(end.toDate())
    }
  }

  isOwner() {
    return this.roleService.getRole('OWNER')
  }

  filterRentals(rentals: Rental[]) {
    if (this.isOwner()) {
      rentals = rentals.filter((rental) => (rental.owners || []).includes(this.auth.user.id))
    }

    return rentals
  }

  close() {
    this.dialogRef.close()
  }

  onRollingChanged({ checked }: MatCheckboxChange) {
    this.form.patchValue({ rolling: !checked ? 0 : moment().unix() })
  }

  onChangeColor(color) {
    this.form.patchValue({ color })
  }

  @SaveForm()
  create(form: FormGroup) {
    this.store.dispatch(AddHoldEvent({ form: form.getRawValue() as HoldForm }))
    this.close()
  }
}
